import { css } from 'aphrodite';
import React, { useEffect, useMemo, useState } from 'react';

import { orgsApi, rolesApi, RoleSummary } from '@/features/org/api/orgs.api';
import { orgStyles as s } from '@/features/org/components/org.styles';

/**
 * Modal: create an invite. Two flavours:
 *   - "Email invite" — restricted to one address, and emailed to it. The
 *                      URL is shown too, for when the email can't be sent.
 *   - "Shareable link" — multi-use, any logged-in user can redeem.
 *
 * On successful create the URL is displayed inline with a copy button.
 * The token is only retrievable once; closing the modal loses it.
 *
 * The Role dropdown is populated from the org's ACTUAL built-in roles
 * (rolesApi.list) rather than a hardcoded list — so the real "PM" role is
 * offered alongside Admin / Developer / Viewer. Only built-in roles are
 * invitable (the invite-create API takes a bare built-in slug); custom
 * roles and `owner` are excluded — ownership is transferred, not invited.
 */

// The bare built-in slugs the invite-create API accepts.
type InviteRole = 'admin' | 'developer' | 'pm' | 'viewer';

type RoleOption = { slug: InviteRole; label: string };

// Priority order for the built-in slugs; higher sorts first. `owner` is
// never invitable so it isn't listed.
const BUILTIN_ORDER: Record<string, number> = { admin: 4, developer: 3, pm: 2, viewer: 1 };

// Fallback options, used only until rolesApi resolves (or if it fails).
const FALLBACK_ROLE_OPTIONS: RoleOption[] = [
  { slug: 'admin', label: 'Admin — manage members + prod releases' },
  { slug: 'developer', label: 'Developer — apps + dev releases' },
  { slug: 'pm', label: 'PM — publish Live Layer + edit content' },
  { slug: 'viewer', label: 'Viewer — read-only' },
];

export default function InviteModal({
  orgSlug,
  onClose,
  onCreated,
}: {
  orgSlug: string;
  onClose: () => void;
  onCreated?: () => void;
}) {
  const [kind, setKind] = useState<'email' | 'link'>('link');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<InviteRole>('developer');
  const [roles, setRoles] = useState<RoleSummary[] | null>(null);
  const [expiresInDays, setExpiresInDays] = useState<number>(7);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);
  // Email invites: who it went to, and whether the email actually went out.
  const [emailed, setEmailed] = useState<{ to: string; sent: boolean } | null>(null);
  const [copied, setCopied] = useState(false);

  // Load the org's real roles so the dropdown reflects actual built-in
  // roles instead of a hardcoded list. Needs the org id (roles are keyed
  // by org id).
  useEffect(() => {
    let cancelled = false;
    orgsApi
      .bySlug(orgSlug)
      .then((r) => rolesApi.list(r.org.id))
      .then((r) => {
        if (!cancelled) setRoles(r.roles);
      })
      .catch(() => {
        /* fall back to FALLBACK_ROLE_OPTIONS below */
      });
    return () => {
      cancelled = true;
    };
  }, [orgSlug]);

  // Assignable options — the org's real built-in roles (owner excluded,
  // deduped by slug), highest priority first. Custom roles aren't invitable.
  const roleOptions = useMemo<RoleOption[]>(() => {
    if (!roles) return FALLBACK_ROLE_OPTIONS;
    const seen = new Set<string>();
    const opts: RoleOption[] = [];
    for (const r of roles) {
      if (!r.isBuiltin || r.id === 'owner' || seen.has(r.id)) continue;
      if (!(r.id in BUILTIN_ORDER)) continue; // guard against unknown built-ins
      seen.add(r.id);
      opts.push({
        slug: r.id as InviteRole,
        label: r.description ? `${r.name} — ${r.description}` : r.name,
      });
    }
    opts.sort((a, b) => (BUILTIN_ORDER[b.slug] || 0) - (BUILTIN_ORDER[a.slug] || 0));
    return opts.length ? opts : FALLBACK_ROLE_OPTIONS;
  }, [roles]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const res = await orgsApi.invites.create(orgSlug, {
        roleSlug: role,
        email: kind === 'email' ? email.trim() : undefined,
        isMultiUse: kind === 'link',
        expiresInDays,
      });
      setCreatedUrl(res.url);
      if (kind === 'email') setEmailed({ to: email.trim(), sent: !!res.emailSent });
      if (onCreated) onCreated();
    } catch (e: any) {
      setErr(e.message || 'Failed to create invite');
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    if (!createdUrl) return;
    try {
      await navigator.clipboard.writeText(createdUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* Clipboard may be unavailable in non-secure contexts. Fall through. */
    }
  };

  return (
    <div className={css(s.modalBackdrop)} onClick={onClose}>
      <div className={css(s.modal)} onClick={(e) => e.stopPropagation()}>
        <div className={css(s.modalTitle)}>
          {!createdUrl ? 'Invite a member' : emailed?.sent ? 'Invite sent' : 'Invite created'}
        </div>
        <div className={css(s.modalDesc)}>
          {!createdUrl
            ? 'Pick how to send the invite. Email invites are sent to one address and only work for it; shareable links can be used by anyone with the URL.'
            : emailed?.sent
            ? `We emailed an invite to ${emailed.to}. You can also share this link yourself — it cannot be retrieved later.`
            : emailed
            ? `We couldn't email ${emailed.to}. Copy this link and send it to them yourself — it cannot be retrieved later.`
            : "Copy this link and share it with the person you're inviting. It cannot be retrieved later."}
        </div>
        {err && <div className={css(s.errorBox)}>{err}</div>}

        {createdUrl ? (
          <>
            <div className={css(s.copyBox)}>{createdUrl}</div>
            <div className={css(s.modalActions)}>
              <button type="button" className={css(s.btnGhost)} onClick={onClose}>
                Done
              </button>
              <button type="button" className={css(s.btn)} onClick={handleCopy}>
                {copied ? 'Copied!' : 'Copy link'}
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className={css(s.field)}>
              <label className={css(s.label)}>Type</label>
              <select
                className={css(s.select)}
                value={kind}
                onChange={(e) => setKind(e.target.value as 'email' | 'link')}
              >
                <option value="link">Shareable link (multi-use)</option>
                <option value="email">Email invite (one user)</option>
              </select>
            </div>
            {kind === 'email' && (
              <div className={css(s.field)}>
                <label className={css(s.label)}>Email</label>
                <input
                  className={css(s.input)}
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="teammate@example.com"
                />
              </div>
            )}
            <div className={css(s.field)}>
              <label className={css(s.label)}>Role</label>
              <select
                className={css(s.select)}
                value={role}
                onChange={(e) => setRole(e.target.value as InviteRole)}
              >
                {roleOptions.map((o) => (
                  <option key={o.slug} value={o.slug}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={css(s.field)}>
              <label className={css(s.label)}>Expires</label>
              <select
                className={css(s.select)}
                value={String(expiresInDays)}
                onChange={(e) => setExpiresInDays(parseInt(e.target.value, 10))}
              >
                <option value="1">1 day</option>
                <option value="7">7 days</option>
                <option value="30">30 days</option>
                <option value="90">90 days</option>
              </select>
            </div>
            <div className={css(s.modalActions)}>
              <button type="button" className={css(s.btnGhost)} onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className={css(s.btn)} disabled={busy}>
                {busy ? 'Creating…' : 'Create invite'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
